import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { translations } from '../../../../utils/language';
import { GET } from '../../../../services/api';
import { Button } from '../../../../empreenderUI/ui/Button';
import { Checkbox } from '../../../../empreenderUI/ui/Checkbox';
import ContentChip from './ContentChip';
import ModalConfirmation from '../../../../components/Modal-Confirmation';
import { Loader2, Plus } from 'lucide-react';

const PER_PAGE = 40;
const MAX_LIST_HEIGHT = 760;

/** @type {Map<string|number, { items: any[], page: number, lastPage: number, total: number }>} */
const variantsCache = new Map();

export function getVariantsCache(productId) {
  return variantsCache.get(productId) || null;
}

export function clearVariantsCache() {
  variantsCache.clear();
}

export function variantKey(item, index) {
  return String(item?.variants?.id ?? `${item?.variants?.name || 'v'}-${index}`);
}

function deliveryMedias(variant) {
  return (variant?.media || []).filter((m) => m.is_preview === false || m.is_preview === 0 || m.is_preview === '0');
}

function previewMedias(variant) {
  return (variant?.media || []).filter((m) => m.is_preview === true || m.is_preview === 1 || m.is_preview === '1');
}

function mediaLabel(media, lang) {
  if (media.type === 'url') return media.media_name || media.media || translations['products-link-singular'][lang];
  return media.media_name || media.media || translations['products-file-singular'][lang];
}

function parseVariantsResponse(result) {
  const body = result?.data?.data || result?.data || {};
  const list = body.variants || [];
  const meta = body.meta || {};
  const page = Number(meta.current_page) || 1;
  const lastPage = Number(meta.last_page) || 1;
  const total = Number(meta.total) || list.length;
  return { list, page, lastPage, total };
}

function VariantsSkeleton({ rows = 2, lang = 'pt' }) {
  return (
    <div className='border-t border-gray-100 w-full min-h-[180px]' aria-busy='true' aria-label={translations['products-loading-variants'][lang]}>
      <div className='overflow-hidden'>
        <div className='bg-[#fafbfd] px-4 sm:px-6 py-3 flex gap-4'>
          <div className='h-2.5 w-20 rounded bg-gray-200 animate-pulse' />
          <div className='h-2.5 w-40 rounded bg-gray-200 animate-pulse' />
          <div className='h-2.5 w-24 rounded bg-gray-200 animate-pulse' />
        </div>
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className='px-4 sm:px-6 py-5 border-b border-gray-100 grid grid-cols-[22%_48%_30%] gap-3'>
            <div className='h-4 w-3/4 max-w-[140px] rounded bg-gray-200 animate-pulse' />
            <div className='flex gap-2'>
              <div className='h-8 w-28 rounded-md bg-gray-200 animate-pulse' />
              <div className='h-8 w-8 rounded-md bg-gray-100 animate-pulse' />
            </div>
            <div className='h-8 w-28 rounded-md bg-gray-100 animate-pulse' />
          </div>
        ))}
      </div>
      <div className='px-6 py-4 border-t border-gray-100'>
        <div className='h-4 w-48 rounded bg-gray-100 animate-pulse' />
      </div>
    </div>
  );
}

export default function VariantsExpandPanel({ product, onAddContent, onAddPreview, onEditLicense, refreshToken = 0, selectedKeys = new Set(), onSelectedKeysChange }) {
  const dispatch = useDispatch();
  const language = useSelector((state) => state.reducerLanguage);
  const lang = language?.data || 'pt';
  const productId = product?.products?.product_id;
  const estimatedRows = Math.max(1, Math.min(Number(product?.variants) || 2, 4));

  const cached = productId != null ? variantsCache.get(productId) : null;
  const [loading, setLoading] = useState(!cached);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [variants, setVariants] = useState(cached?.items || []);
  const [page, setPage] = useState(cached?.page || 0);
  const [lastPage, setLastPage] = useState(cached?.lastPage || 1);
  const [total, setTotal] = useState(cached?.total || 0);
  const [contentReady, setContentReady] = useState(!!cached);
  const [deleteMedia, setDeleteMedia] = useState(null);
  const [deleteLicense, setDeleteLicense] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const setSelectedKeys = useCallback(
    (updater) => {
      const prev = selectedKeys instanceof Set ? selectedKeys : new Set(selectedKeys || []);
      const next = typeof updater === 'function' ? updater(prev) : updater;
      onSelectedKeysChange?.(next instanceof Set ? next : new Set(next || []));
    },
    [onSelectedKeysChange, selectedKeys],
  );

  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const fetchingRef = useRef(false);

  const hasMore = page < lastPage;

  const persistCache = useCallback(
    (next) => {
      if (productId == null) return;
      variantsCache.set(productId, next);
    },
    [productId],
  );

  const fetchPage = useCallback(
    async (pageToLoad, { replace = false } = {}) => {
      if (!productId || fetchingRef.current) return null;
      fetchingRef.current = true;

      try {
        const result = await GET({
          endpoint: `/variants/${productId}?page=${pageToLoad}&perPage=${PER_PAGE}`,
        });
        if (!result || !result.data) return null;

        const { list, page: current, lastPage: last, total: tot } = parseVariantsResponse(result);

        setVariants((prev) => {
          const merged = replace ? list : [...prev, ...list];
          persistCache({ items: merged, page: current, lastPage: last, total: tot });
          return merged;
        });
        setPage(current);
        setLastPage(last);
        setTotal(tot);
        return { list, page: current, lastPage: last, total: tot };
      } catch {
        return null;
      } finally {
        fetchingRef.current = false;
      }
    },
    [persistCache, productId],
  );

  const loadInitial = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    setContentReady(false);
    setSelectedKeys(new Set());
    await fetchPage(1, { replace: true });
    setLoading(false);
    requestAnimationFrame(() => setContentReady(true));
  }, [fetchPage, productId]);

  const loadMore = useCallback(async () => {
    if (!productId || !hasMore || fetchingRef.current || loading || loadingMore) return;
    setLoadingMore(true);
    await fetchPage(page + 1);
    setLoadingMore(false);
  }, [fetchPage, hasMore, loading, loadingMore, page, productId]);

  useEffect(() => {
    if (!productId) return;

    if (refreshToken > 0) {
      variantsCache.delete(productId);
      setVariants([]);
      setPage(0);
      setLastPage(1);
      setTotal(0);
      setSelectedKeys(new Set());
      loadInitial();
      return;
    }

    const existing = variantsCache.get(productId);
    if (existing?.items?.length) {
      setVariants(existing.items);
      setPage(existing.page);
      setLastPage(existing.lastPage);
      setTotal(existing.total);
      setLoading(false);
      setContentReady(true);
      return;
    }

    loadInitial();
  }, [loadInitial, productId, refreshToken]);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) loadMore();
      },
      { root, rootMargin: '120px', threshold: 0 },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, loadMore, variants.length, contentReady]);

  const confirmUnlink = () => {
    if (deleteLicense) {
      dispatch({
        type: 'DELETE_LINK_LICENSE_PRODUCTS',
        payload: { id: deleteLicense.id },
      });
    } else if (deleteMedia) {
      dispatch({
        type: 'DELETE_LINK_MEDIA_PRODUCTS',
        payload: {
          media_id: deleteMedia.id,
          product_id: productId,
        },
      });
    } else {
      return;
    }

    setConfirmOpen(false);
    setDeleteMedia(null);
    setDeleteLicense(null);
    setTimeout(() => {
      variantsCache.delete(productId);
      loadInitial();
    }, 600);
  };

  const confirmTitle = deleteLicense
    ? `${translations['variants-desvincular-licenca'][lang]} ${deleteLicense?.license?.name || translations['products-license-singular'][lang]}?`
    : `${translations['variants-desvincular-midia'][lang]} ${deleteMedia ? mediaLabel(deleteMedia, lang) : ''}?`;

  const loadedKeys = useMemo(() => variants.map((item, index) => variantKey(item, index)), [variants]);

  const selectedCount = selectedKeys.size;
  const allLoadedSelected = loadedKeys.length > 0 && loadedKeys.every((key) => selectedKeys.has(key));
  const someLoadedSelected = loadedKeys.some((key) => selectedKeys.has(key)) && !allLoadedSelected;

  const selectedVariants = useMemo(() => variants.filter((item, index) => selectedKeys.has(variantKey(item, index))), [variants, selectedKeys]);

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleAllLoaded = (checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        loadedKeys.forEach((key) => next.add(key));
      } else {
        loadedKeys.forEach((key) => next.delete(key));
      }
      return next;
    });
  };

  const loadAndSelectAll = async () => {
    if (selectingAll) return;
    setSelectingAll(true);
    try {
      let current = page;
      let last = lastPage;
      let items = [...variants];

      while (current < last) {
        const nextPage = current + 1;
        const result = await GET({
          endpoint: `/variants/${productId}?page=${nextPage}&perPage=${PER_PAGE}`,
        });
        if (!result?.data) break;
        const parsed = parseVariantsResponse(result);
        items = [...items, ...parsed.list];
        current = parsed.page;
        last = parsed.lastPage;
        setVariants(items);
        setPage(current);
        setLastPage(last);
        setTotal(parsed.total);
        persistCache({ items, page: current, lastPage: last, total: parsed.total });
      }

      const keys = items.map((item, index) => variantKey(item, index));
      setSelectedKeys(new Set(keys));
    } finally {
      setSelectingAll(false);
    }
  };

  const selectionLabel = selectedCount === 1 ? selectedVariants[0]?.variants?.name || `1 ${translations['products-variant-singular'][lang]}` : `${selectedCount} ${translations['products-variants-label'][lang]}`;

  const skeletonRows = useMemo(() => estimatedRows, [estimatedRows]);
  const shownTotal = total || variants.length;

  if (loading && !variants.length) {
    return <VariantsSkeleton rows={skeletonRows} lang={lang} />;
  }

  if (!loading && !variants.length) {
    return <div className='border-t border-gray-100 px-6 py-8 text-sm text-gray-500 text-center min-h-[120px] flex items-center justify-center'>{translations['products-no-variants'][lang]}</div>;
  }

  return (
    <div className={`border-t border-gray-100 w-full max-w-full min-w-0 min-h-[180px] transition-opacity duration-200 ease-out ${contentReady ? 'opacity-100' : 'opacity-0'}`}>
      <ModalConfirmation
        confirmation={confirmUnlink}
        setVisibleModal={(open) => {
          setConfirmOpen(open);
          if (!open) {
            setDeleteMedia(null);
            setDeleteLicense(null);
          }
        }}
        visible={confirmOpen}
        title={confirmTitle}
      />

      <div ref={scrollRef} className='overflow-auto w-full max-w-full min-w-0 thin-scrollbar' style={{ maxHeight: MAX_LIST_HEIGHT }}>
        <table className='w-full min-w-[680px] text-left table-fixed'>
          <thead className='sticky top-0 z-[1]'>
            <tr className='bg-[#fafbfd] text-[10px] uppercase tracking-wide text-gray-400 font-semibold'>
              <th className='px-3 sm:px-4 py-3 w-12'>
                <Checkbox checked={allLoadedSelected ? true : someLoadedSelected ? 'indeterminate' : false} onCheckedChange={(checked) => toggleAllLoaded(checked === true)} aria-label={translations['products-select-all-loaded-aria'][lang]} />
              </th>
              <th className='px-2 sm:px-4 py-3 w-[20%]'>{translations['products-variant-singular'][lang]}</th>
              <th className='px-4 sm:px-6 py-3 w-[46%]'>{translations['products-th-delivery-content'][lang]}</th>
              <th className='px-4 sm:px-6 py-3 w-[28%]'>{translations['products-th-pdf-preview'][lang]}</th>
            </tr>
          </thead>
          <tbody>
            {variants.map((item, index) => {
              const key = variantKey(item, index);
              const selected = selectedKeys.has(key);
              const delivery = deliveryMedias(item);
              const previews = previewMedias(item);
              const licenses = (item?.variants?.variantLinkLicenses || []).filter(
                (link) => link?.license && link.license.active !== 0 && link.license.active !== false,
              );

              return (
                <tr key={key} className={`border-b border-gray-100 ${selected ? 'bg-blue-50/40' : ''}`}>
                  <td className='px-3 sm:px-4 py-5 align-middle'>
                    <Checkbox checked={selected} onCheckedChange={() => toggleOne(key)} aria-label={`${translations['products-select-aria'][lang]} ${item?.variants?.name || translations['products-variant-singular'][lang].toLowerCase()}`} />
                  </td>
                  <td className='px-2 sm:px-4 py-5 text-sm font-medium text-gray-900 truncate'>{item?.variants?.name || '—'}</td>
                  <td className='px-4 sm:px-6 py-5'>
                    <div className='flex flex-wrap items-center gap-1 min-w-0'>
                      {delivery.map((media) => (
                        <ContentChip
                          key={media.id}
                          media={media}
                          label={mediaLabel(media, lang)}
                          onRemove={() => {
                            setDeleteMedia(media);
                            setDeleteLicense(null);
                            setConfirmOpen(true);
                          }}
                        />
                      ))}
                      {licenses.map((link) => (
                        <ContentChip
                          key={link.id}
                          media={{ kind: 'license' }}
                          label={link?.license?.name || translations['products-license-singular'][lang]}
                          onClick={() => {
                            if (!link?.license) return;
                            onEditLicense?.({
                              license: link.license,
                              variant: item,
                              linkId: link.id,
                            });
                          }}
                          onRemove={() => {
                            setDeleteLicense(link);
                            setDeleteMedia(null);
                            setConfirmOpen(true);
                          }}
                        />
                      ))}
                      <Button
                        type='button'
                        variant='outline'
                        size={delivery.length || licenses.length ? 'icon' : 'default'}
                        onClick={() => onAddContent?.([item])}
                        className={delivery.length || licenses.length ? 'size-8 border-dashed border-blue-200  hover:bg-blue-50' : 'h-auto border-dashed border-blue-200  px-2.5 py-2 !text-xs hover:bg-blue-50'}
                      >
                        {delivery.length || licenses.length ? <Plus className='size-3.5' /> : `+ ${translations['products-add-content'][lang]}`}
                      </Button>
                    </div>
                  </td>
                  <td className='px-4 sm:px-6 py-5'>
                    <div className='flex flex-wrap items-center gap-1 min-w-0'>
                      {previews.map((media) => (
                        <ContentChip
                          key={media.id}
                          media={media}
                          label={mediaLabel(media, lang)}
                          onRemove={() => {
                            setDeleteMedia(media);
                            setDeleteLicense(null);
                            setConfirmOpen(true);
                          }}
                        />
                      ))}
                      {!previews.length && (
                        <Button type='button' variant='outline' onClick={() => onAddPreview?.([item])} className='h-auto border-dashed border-blue-200  px-2.5 py-2 !text-xs hover:bg-blue-50'>
                          {translations['products-add-pdf'][lang]}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div ref={sentinelRef} className='h-4 w-full' aria-hidden />

        {loadingMore && (
          <div className='flex items-center justify-center gap-2 py-3 text-xs text-gray-500'>
            <Loader2 className='size-3.5 animate-spin' />
            {translations['products-loading-more-variants'][lang]}
          </div>
        )}
      </div>

      <div className='px-4 sm:px-6 py-4 flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 bg-white'>
        <span className='text-sm text-gray-500'>{selectedCount > 0 ? `${selectionLabel} ${translations['products-selected-suffix'][lang]}` : translations['products-select-variants-hint'][lang]}</span>
        <div className='flex flex-wrap items-center gap-3 text-xs text-gray-400'>
          {hasMore && (
            <Button variant='ghost' className='h-auto px-0 text-xs text-blue-600 hover:text-blue-700' disabled={selectingAll} onClick={loadAndSelectAll}>
              {selectingAll ? (
                <span className='inline-flex items-center gap-1.5'>
                  <Loader2 className='size-3 animate-spin' />
                  {translations['products-selecting-all'][lang]}
                </span>
              ) : (
                `${translations['products-select-all'][lang]} (${shownTotal})`
              )}
            </Button>
          )}
          {!hasMore && loadedKeys.length > 0 && !allLoadedSelected && (
            <Button variant='ghost' className='h-auto px-0 text-xs text-blue-600 hover:text-blue-700' onClick={() => toggleAllLoaded(true)}>
              {translations['products-select-all'][lang]} ({shownTotal})
            </Button>
          )}
          <span>
            {variants.length} {translations['products-pagination-de'][lang]} {shownTotal} {translations['products-variants-label'][lang]}
            {hasMore ? ` · ${translations['products-scroll-load-more'][lang]}` : ''}
          </span>
        </div>
      </div>
    </div>
  );
}
